import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axios-auth';
import { NEUTRAL_PROFILE, presentationToCssVars, } from '../utils/learner-presentation';
import { cn } from '../lib/utils';
const LearnerPresentationContext = createContext(null);
function isMeetingPath(pathname) {
    return pathname.includes('/video-meeting');
}
export function useLearnerPresentation() {
    const ctx = useContext(LearnerPresentationContext);
    if (!ctx) {
        return {
            profile: NEUTRAL_PROFILE,
            frozen: false,
            loading: false,
            refresh: async () => null,
            save: async () => null,
        };
    }
    return ctx;
}
export function LearnerPresentationProvider({ children, learnerId }) {
    const user = useSelector((s) => s.auth.user);
    const location = useLocation();
    const hideOverlay = isMeetingPath(location.pathname);
    const resolvedLearnerId = learnerId || (user?.role === 'student' ? user.id : null);
    const [profile, setProfile] = useState(NEUTRAL_PROFILE);
    const [frozen, setFrozen] = useState(false);
    const [loading, setLoading] = useState(false);
    const fetchedRef = useRef(null);
    const refresh = useCallback(async (opts) => {
        if (!resolvedLearnerId)
            return null;
        const applyToSession = opts?.applyToSession ?? !opts?.contextId;
        if (applyToSession)
            setLoading(true);
        try {
            const params = new URLSearchParams();
            if (opts?.surface)
                params.set('surface', opts.surface);
            if (opts?.contextId)
                params.set('context_id', opts.contextId);
            if (opts?.assessmentType)
                params.set('assessment_type', opts.assessmentType);
            if (opts?.freeze)
                params.set('freeze', 'true');
            const qs = params.toString();
            const res = await axiosInstance.get(`/learner-presentation/${resolvedLearnerId}${qs ? `?${qs}` : ''}`);
            const payload = res.data?.data;
            const resolved = payload && 'resolved' in payload
                ? payload
                : payload
                    ? { resolved: payload, frozen: false, application_id: null, applied_at: null }
                    : null;
            if (applyToSession && resolved?.resolved) {
                setProfile({ ...NEUTRAL_PROFILE, ...resolved.resolved });
                setFrozen(Boolean(resolved.frozen));
            }
            return resolved;
        }
        catch {
            return null;
        }
        finally {
            if (applyToSession)
                setLoading(false);
        }
    }, [resolvedLearnerId]);
    const save = useCallback(async (patch, extras) => {
        const id = extras?.learnerId || resolvedLearnerId;
        if (!id)
            return null;
        const res = await axiosInstance.put(`/learner-presentation/${id}`, {
            patch,
            rationale: extras?.rationale,
            set_by_role: extras?.setByRole,
        });
        const saved = res.data?.data;
        if (saved && (!extras?.learnerId || extras.learnerId === resolvedLearnerId)) {
            setProfile({ ...NEUTRAL_PROFILE, ...saved });
        }
        return saved ?? null;
    }, [resolvedLearnerId]);
    useEffect(() => {
        if (!resolvedLearnerId) {
            setProfile(NEUTRAL_PROFILE);
            setFrozen(false);
            fetchedRef.current = null;
            return;
        }
        if (fetchedRef.current === resolvedLearnerId)
            return;
        fetchedRef.current = resolvedLearnerId;
        void refresh();
    }, [resolvedLearnerId, refresh]);
    const value = useMemo(() => ({ profile, frozen, loading, refresh, save }), [profile, frozen, loading, refresh, save]);
    const cssVars = presentationToCssVars(profile);
    const className = cn('lp-root', profile.single_column && 'lp-single-column', profile.large_hit_targets && 'lp-large-hit', profile.reduced_motion && 'lp-reduced-motion', profile.keyboard_only && 'lp-keyboard-only');
    return (_jsx(LearnerPresentationContext.Provider, { value: value, children: _jsx("div", { className: className, style: cssVars, "data-lp-font": profile.font_family, "data-lp-meeting": hideOverlay ? 'true' : undefined, children: children }) }));
}
